import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, extractErrorMessage } from '../../Services/authClient';

const FeedPage = () => {
  const { t } = useTranslation();
  const [videos, setVideos] = useState([]);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(t('app.feed.loading', 'Loading feed…'));
  const [newPost, setNewPost] = useState('');
  const [activePost, setActivePost] = useState(null);
  const [commentValue, setCommentValue] = useState('');

  useEffect(() => {
    setStatus(t('app.feed.loading', 'Loading feed…'));
  }, [t]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([loadVideos(), loadPosts()]).finally(() => setIsLoading(false));
  }, []);

  const loadVideos = async () => {
    try {
      const data = await apiFetch('/meanc/videos-display/');
      setVideos(Array.isArray(data) ? data : []);
    } catch (error) {
      setStatus(extractErrorMessage(error, t('app.feed.videosError', 'Unable to load videos.')));
    }
  };

  const loadPosts = async () => {
    try {
      const data = await apiFetch('/meanc/posts/');
      setPosts(Array.isArray(data) ? data : data?.results || []);
      setStatus('');
    } catch (error) {
      setStatus(extractErrorMessage(error, t('app.feed.postsError', 'Unable to load posts.')));
    }
  };

  const handleCreatePost = async (event) => {
    event.preventDefault();
    if (!newPost.trim()) return;
    try {
      await apiFetch('/meanc/posts/', {
        method: 'POST',
        body: JSON.stringify({ content: newPost }),
      });
      setNewPost('');
      loadPosts();
    } catch (error) {
      setStatus(extractErrorMessage(error, t('app.feed.postError', 'Unable to publish post.')));
    }
  };

  const handleSendComment = async (postId) => {
    if (!commentValue.trim()) return;
    try {
      await apiFetch('/meanc/comment/', {
        method: 'POST',
        body: JSON.stringify({ post: postId, content: commentValue }),
      });
      setCommentValue('');
      loadPosts();
    } catch (error) {
      setStatus(extractErrorMessage(error, t('app.feed.commentError', 'Unable to send comment.')));
    }
  };

  const getPostId = (post) => post?.id ?? post?.pk;
  const commentsForPost = (post) => post?.comments || post?.comment_set || [];

  const videoList = useMemo(() => videos.slice(0, 6), [videos]);

  return (
    <div className="feed">
      <div className="feed__grid">
        <section className="feed__main">
          <form className="feed__composer" onSubmit={handleCreatePost}>
            <textarea
              placeholder={t('app.feed.placeholder', 'Share scouting notes, a win recap, or motivation.')}
              value={newPost}
              onChange={(event) => setNewPost(event.target.value)}
            />
            <button type="submit">{t('app.feed.postButton', 'Post update')}</button>
          </form>

          {status && <p className="feed__status">{status}</p>}

          {posts.map((post) => (
            <article key={getPostId(post)} className="feed-card">
              <header>
                <div>
                  <p className="feed-card__title">{post.title || post.topic || t('app.feed.untitled', 'Untitled post')}</p>
                  <small>#{getPostId(post)}</small>
                </div>
                <span>{new Date(post.created_at || post.timestamp || Date.now()).toLocaleString()}</span>
              </header>
              <p>{post.content || post.body || post.text}</p>
              {post.video_url && (
                <video src={post.video_url} controls preload="metadata" />
              )}
              <footer>
                <button type="button" onClick={() => setActivePost((prev) => (prev === getPostId(post) ? null : getPostId(post)))}>
                  {t('app.feed.commentsCount', '{{count}} comments', { count: commentsForPost(post).length })}
                </button>
              </footer>
              {activePost === getPostId(post) && (
                <div className="feed-card__comments">
                  <div className="feed-card__comments-list">
                    {commentsForPost(post).map((comment) => (
                      <div key={comment.id}>
                        <strong>{comment.author_name || comment.user || 'User'}:</strong> {comment.content || comment.text || comment.body}
                      </div>
                    ))}
                    {commentsForPost(post).length === 0 && <p>{t('app.feed.noComments', 'No comments yet. Be the first.')}</p>}
                  </div>
                  <div className="feed-card__comment-box">
                    <textarea
                      placeholder={t('app.feed.commentPlaceholder', 'Drop insight')}
                      value={commentValue}
                      onChange={(event) => setCommentValue(event.target.value)}
                    />
                    <button type="button" onClick={() => handleSendComment(getPostId(post))}>{t('app.feed.commentButton', 'Comment')}</button>
                  </div>
                </div>
              )}
            </article>
          ))}
        </section>

        <aside className="feed__sidebar">
          <h3>{t('app.feed.videoBoard', 'Video board')}</h3>
          {isLoading && <p>{t('app.feed.loadingVideos', 'Loading videos…')}</p>}
          {videoList.map((video) => (
            <div key={video.id} className="video-card">
              <video src={video.video_url || video.file} controls preload="metadata" />
              <div>
                <p className="video-card__title">{video.title || video.name || t('app.feed.videoFallback', 'Match highlight')}</p>
                <small>{video.category || video.team}</small>
              </div>
            </div>
          ))}
        </aside>
      </div>
    </div>
  );
};

export default FeedPage;
